import React, { useEffect, useState } from "react";
import LabelWithInput from "@components/input/LabelWithInput";
import { ColorDropdown } from "@pages/register/_components/index";
import styled from "@emotion/styled";
import FadeInContainer from "@components/motion/FadeInContainer";
import { useFormContext } from "react-hook-form";
import SizeDropdown from "@pages/register/_components/SizeDropdown";
import useSelectedColorStore from "@store/register/useSelectedColor";
import Image from "@components/item/Image";
import { useTheme } from "@emotion/react";
import { useQuery } from "@tanstack/react-query";
import { useApiRequest } from "@utils/ApiRequest";
import { useLocation } from "react-router-dom";
import {
  findColorOptionIndex,
  getColorOptionIdentity,
} from "@utils/productClassification";

// size_type.name 은 영문 약어라 그대로 노출하면 알아보기 어렵다.
// 라벨 문구는 owl-admin 의 사이즈 체계 선택지와 맞춘다.
const SIZE_TYPE_LABELS: Record<string, string> = {
  Clothing: "기본 (Free~3XL)",
  WomenBottom: "여성 하의 (23~34)",
  MenBottom: "남성 하의 (28~40)",
  KrUni: "한국식 유니 (80~115)",
  KidsKr: "아동복 한국식 (60~160)",
  WomenShoes: "여성 신발 (225~260)",
  MenShoes: "남성 신발 (250~300)",
  Free: "사이즈 없음 (FREE)",
};

const ColorWithSize = ({
  stockListRef,
}: {
  stockListRef?: any;
  showSizeError?: boolean;
}) => {
  const [openDropdown, setOpenDropdown] = useState(false);
  const [openSizeDropdown, setOpenSizeDropdown] = useState(false);
  const [selectedColor, setSelectedColor] = useState("선택");
  const theme = useTheme();
  const {
    watch,
    setValue,
    formState: { errors },
  } = useFormContext();
  const {
    color,
    setColor,
    setColorId,
    colorOptionKey,
    setColorOptionKey,
  } = useSelectedColorStore();
  const { getSizeList, getSizeTypeList } = useApiRequest();

  const sizeTypeId = watch("size_type_id");
  const watchedStockList = watch("stock_list") || [];
  const stockList = watchedStockList;
  const activeIndex = colorOptionKey
    ? findColorOptionIndex(stockList, colorOptionKey)
    : stockList.findIndex((item: any) => item.color_name === color);
  const activeStock = activeIndex >= 0 ? stockList[activeIndex] : null;

  const { data: sizeList, isFetching: isSizeListLoading } = useQuery({
    queryKey: ["sizeList", color, sizeTypeId],
    queryFn: () => getSizeList(sizeTypeId),
    enabled: !!sizeTypeId,
  });

  const { data: sizeTypeList = [] } = useQuery({
    queryKey: ["sizeTypeList"],
    queryFn: getSizeTypeList,
  });

  // 사이즈 체계는 대분류와 무관하게 직접 고른다.
  // 공용·잡화처럼 카테고리에 사이즈 체계가 없어도 입력할 수 있어야 하고,
  // 대분류를 잘못 골랐을 때 처음부터 다시 쓰지 않고 여기서 바꿀 수 있어야 한다.
  const handleChangeSizeType = (nextSizeTypeId: number) => {
    if (Number(nextSizeTypeId) === Number(sizeTypeId)) return;

    setValue("size_type_id", nextSizeTypeId, { shouldValidate: true });
    // 체계가 바뀌면 이전 체계의 size_id 는 더 이상 유효하지 않으므로 비운다.
    stockList.forEach((_: any, index: number) => {
      setValue(`stock_list.${index}.size_list`, []);
    });
    setOpenSizeDropdown(false);
  };

  useEffect(() => {
    if (stockList.length === 0) {
      setColor("");
      setColorId(0);
      setColorOptionKey("");
      setSelectedColor("선택");
      setOpenSizeDropdown(false);
      return;
    }

    const selectedItem = colorOptionKey
      ? stockList.find((item: any) => getColorOptionIdentity(item) === colorOptionKey)
      : stockList.find((item: any) => item.color_name === color);
    if (selectedItem) return;

    const fallback = stockList[0];
    setColor(fallback.color_name);
    setColorId(fallback.color_id);
    setColorOptionKey(getColorOptionIdentity(fallback));
  }, [stockList, color, colorOptionKey, setColor, setColorId, setColorOptionKey]);

  const handleClickColorBox = (selected: any) => {
    setColor(selected.color_name);
    setColorId(selected.color_id ?? 0);
    setColorOptionKey(getColorOptionIdentity(selected));
    setOpenSizeDropdown(false);
  };

  const handleDelete = (e: React.MouseEvent, optionKey: string, colorName: string) => {
    e.stopPropagation();
    const updatedProductInfo = stockList.filter(
      (productInfo: any) =>
        optionKey ? productInfo.option_key !== optionKey : productInfo.color_name !== colorName,
    );

    if (updatedProductInfo.length > 0) {
      const nextColor = updatedProductInfo[updatedProductInfo.length - 1];
      setSelectedColor(nextColor.color_name);
      setColor(nextColor.color_name);
      setColorId(nextColor.color_id);
      setColorOptionKey(getColorOptionIdentity(nextColor));
    } else {
      setSelectedColor("선택");
      setColor("");
      setColorId(0);
      setColorOptionKey("");
      setOpenSizeDropdown(false);
    }

    setValue("stock_list", updatedProductInfo);
  };

  const { pathname } = useLocation();
  const isProductEdit = pathname === "/product-edit";

  const stockListError = errors?.stock_list;
  const currentColorError =
    activeIndex >= 0 && Array.isArray(stockListError)
      ? stockListError[activeIndex]
      : null;

  const sizeListError = currentColorError?.size_list;
  const sizeListMessage =
    sizeListError && !Array.isArray(sizeListError)
      ? sizeListError.message
      : null;

  const summarizeSizes = (sizeItems: any[] = []) => {
    const totalAmount = sizeItems.reduce(
      (sum, item) => sum + (Number(item?.amount) || 0),
      0,
    );
    const names = sizeItems
      .map((item) => item?.size_name)
      .filter(Boolean)
      .slice(0, 2);

    return {
      totalAmount,
      names,
      count: sizeItems.length,
      extraCount: Math.max(sizeItems.length - names.length, 0),
    };
  };

  return (
    <Container ref={stockListRef}>
      <LabelWithInput label="색상" required>
        <SectionShell>
          <SectionHeader>
            <SectionTitle>선택한 색상별로 사이즈와 수량을 입력하세요.</SectionTitle>
            <SectionSubtitle>
              색상을 추가하면 아래 카드에서 선택 상태와 입력 현황을 바로 확인할 수 있습니다.
            </SectionSubtitle>
          </SectionHeader>

          <ColorDropdown
            name="stock_list"
            open={openDropdown}
            setOpen={setOpenDropdown}
            selectedValue={selectedColor}
            setSelectedValue={setSelectedColor}
          />

          {stockList.length === 0 ? (
            <EmptyState>
              <EmptyStateTitle>아직 선택된 색상이 없습니다.</EmptyStateTitle>
              <EmptyStateText>
                상단에서 색상을 추가하면 카드가 생성되고, 각 색상별 사이즈와 수량을 바로 입력할 수 있습니다.
              </EmptyStateText>
            </EmptyState>
          ) : (
            <>
              <ColorCardGrid>
                {stockList.map((item: any, index: number) => {
                  const { totalAmount, names, count, extraCount } = summarizeSizes(
                    item.size_list || [],
                  );
                  const isSelected = colorOptionKey
                    ? getColorOptionIdentity(item) === colorOptionKey
                    : color === item.color_name;

                  return (
                    <ColorCard
                      key={item.option_key || `${item.color_id}-${item.color_name}-${index}`}
                      selected={isSelected}
                      role="button"
                      tabIndex={0}
                      onClick={() => handleClickColorBox(item)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          handleClickColorBox(item);
                        }
                      }}>
                      <ColorCardTop>
                        <ColorCardIdentity>
                          <ColorBall color={item.hex_code} />
                          <ColorCardNameBlock>
                            <ColorCardNameRow>
                              <ColorCardName>{item.color_name}</ColorCardName>
                              {isSelected ? (
                                <SelectedBadge>선택 중</SelectedBadge>
                              ) : (
                                <MutedBadge>대기</MutedBadge>
                              )}
                            </ColorCardNameRow>
                            <ColorCardSummary>
                              {count > 0 ? (
                                <>
                                  <span>{count}개 사이즈</span>
                                  <SummaryDot />
                                  <span>총 {totalAmount}개</span>
                                </>
                              ) : (
                                <span>사이즈를 추가하지 않았습니다.</span>
                              )}
                            </ColorCardSummary>
                          </ColorCardNameBlock>
                        </ColorCardIdentity>

                        <DeleteButton
                          onClick={(e) => handleDelete(e, item.option_key, item.color_name)}
                          type="button"
                          aria-label={`${item.color_name} 색상 삭제`}>
                          <Image src={theme.image.close.default} size="12px" />
                        </DeleteButton>
                      </ColorCardTop>

                      {count > 0 && (
                        <ColorCardFoot>
                          {names.length > 0 && (
                            <SizePills>
                              {names.map((name) => (
                                <SizePill key={`${item.color_id}-${name}`}>
                                  {name}
                                </SizePill>
                              ))}
                              {extraCount > 0 && <SizePill>+{extraCount}</SizePill>}
                            </SizePills>
                          )}
                        </ColorCardFoot>
                      )}
                    </ColorCard>
                  );
                })}
              </ColorCardGrid>

            </>
          )}
        </SectionShell>
      </LabelWithInput>

      <FadeInContainer isShow={stockList.length !== 0} nonAnimation={isProductEdit}>
        <LabelWithInput label="사이즈" required>
          {activeStock ? (
            <EditorShell>
              <EditorHeader>
                <EditorTitle>
                  <ColorBall color={activeStock.hex_code} />
                  <span>{activeStock.color_name}</span>
                </EditorTitle>
                <EditorSubTitle>
                  현재 선택한 색상의 사이즈와 수량을 입력하세요.
                </EditorSubTitle>
              </EditorHeader>

              <label>컬러 SKU <input maxLength={100} value={activeStock.color_sku || ""} onChange={(event) => setValue(`stock_list.${activeIndex}.color_sku`, event.target.value)} style={{ border: "1px solid #ccc", padding: "1rem" }} /></label>

              <SizeTypeField>
                <SizeTypeLabel>사이즈 체계</SizeTypeLabel>
                <SizeTypeHelp>
                  대분류와 상관없이 선택할 수 있습니다. 체계를 바꾸면 입력한 사이즈는 초기화됩니다.
                </SizeTypeHelp>
                <ChipGroup>
                  {sizeTypeList.map((type: any) => (
                    <SizeTypeChip
                      key={type.id}
                      type="button"
                      active={Number(sizeTypeId) === Number(type.id)}
                      onClick={() => handleChangeSizeType(Number(type.id))}>
                      {SIZE_TYPE_LABELS[type.name] || type.name}
                    </SizeTypeChip>
                  ))}
                </ChipGroup>
              </SizeTypeField>

              <SizeDropdown
                options={sizeList}
                name={`stock_list.${activeIndex}.size_list`}
                open={openSizeDropdown}
                setOpen={setOpenSizeDropdown}
                disabled={!sizeTypeId}
              />

              {!!sizeTypeId &&
                !isSizeListLoading &&
                (sizeList?.length || 0) === 0 && (
                  <HelperMessage>
                    현재 카테고리에 연결된 사이즈 옵션이 없습니다.
                  </HelperMessage>
                )}

              {sizeListMessage && <ErrorMessage>{sizeListMessage}</ErrorMessage>}

              {Array.isArray(sizeListError) &&
                sizeListError.map((sizeError: any, sizeIndex: number) => (
                  <ErrorStack key={sizeIndex}>
                    {sizeError?.size_id?.message && (
                      <ErrorMessage>{sizeError.size_id.message}</ErrorMessage>
                    )}
                    {sizeError?.amount?.message && (
                      <ErrorMessage>{sizeError.amount.message}</ErrorMessage>
                    )}
                  </ErrorStack>
                ))}
            </EditorShell>
          ) : (
            <EditorEmptyState>
              색상을 하나 이상 선택하면 사이즈 입력 영역이 활성화됩니다.
            </EditorEmptyState>
          )}
        </LabelWithInput>
      </FadeInContainer>
    </Container>
  );
};

export default ColorWithSize;

const Container = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: "2rem",
});

const SectionShell = styled.div(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "1.4rem",
  padding: "1.6rem",
  border: `1px solid ${theme.color.border_gray}`,
  borderRadius: "1.6rem",
  background: `linear-gradient(180deg, ${theme.color.white} 0%, ${theme.color.light_mint}12 100%)`,
}));

const SectionHeader = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: "0.4rem",
});

const SectionTitle = styled.div(({ theme }) => ({
  fontSize: "1.5rem",
  fontWeight: theme.fontWeight.medium,
  lineHeight: "2rem",
  color: theme.color.black,
}));

const SectionSubtitle = styled.div(({ theme }) => ({
  fontSize: "1.2rem",
  lineHeight: "1.8rem",
  color: theme.color.light_gray,
}));

const EmptyState = styled.div(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "0.5rem",
  padding: "1.6rem",
  borderRadius: "1.4rem",
  border: `1px dashed ${theme.color.border_gray}`,
  backgroundColor: theme.color.white,
}));

const EmptyStateTitle = styled.div(({ theme }) => ({
  fontSize: "1.4rem",
  fontWeight: theme.fontWeight.medium,
  color: theme.color.black,
}));

const EmptyStateText = styled.div(({ theme }) => ({
  fontSize: "1.2rem",
  lineHeight: "1.8rem",
  color: theme.color.light_gray,
}));

const ColorCardGrid = styled.div({
  display: "grid",
  gridTemplateColumns: "1fr",
  gap: "1rem",
});

const ColorCard = styled.div<{ selected: boolean }>(({ theme, selected }) => ({
  width: "100%",
  display: "flex",
  flexDirection: "column",
  gap: "0.8rem",
  padding: "1.4rem",
  borderRadius: "1.4rem",
  border: `1px solid ${selected ? theme.color.mint : theme.color.border_gray}`,
  backgroundColor: selected ? theme.color.light_mint : theme.color.white,
  boxShadow: selected
    ? "0 0 0 1px rgba(0, 0, 0, 0.02), 0 8px 24px rgba(0, 0, 0, 0.06)"
    : "none",
  cursor: "pointer",
  textAlign: "left",
  transition:
    "transform 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease, background-color 0.18s ease",
  ":hover": {
    transform: "translateY(-1px)",
    borderColor: theme.color.mint,
  },
}));

const ColorCardTop = styled.div({
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: "1rem",
});

const ColorCardIdentity = styled.div({
  display: "flex",
  alignItems: "flex-start",
  gap: "1rem",
  minWidth: 0,
  flex: 1,
});

const ColorBall = styled.div<{ color: string }>(({ color }) => ({
  width: "1.4rem",
  height: "1.4rem",
  borderRadius: "50%",
  backgroundColor: color,
  flexShrink: 0,
  marginTop: "0.4rem",
  boxShadow: "0 0 0 1px rgba(0, 0, 0, 0.08) inset",
}));

const ColorCardNameBlock = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: "0.3rem",
  minWidth: 0,
  flex: 1,
});

const ColorCardNameRow = styled.div({
  display: "flex",
  alignItems: "center",
  gap: "0.6rem",
  flexWrap: "wrap",
});

const ColorCardName = styled.div(({ theme }) => ({
  fontSize: "1.4rem",
  fontWeight: theme.fontWeight.medium,
  lineHeight: "2rem",
  color: theme.color.black,
}));

const SelectedBadge = styled.span(({ theme }) => ({
  padding: "0.2rem 0.7rem",
  borderRadius: "999px",
  backgroundColor: theme.color.mint,
  color: theme.color.white,
  fontSize: "1rem",
  lineHeight: "1.6rem",
  fontWeight: theme.fontWeight.medium,
}));

const MutedBadge = styled.span(({ theme }) => ({
  padding: "0.2rem 0.7rem",
  borderRadius: "999px",
  backgroundColor: theme.color.border_gray,
  color: theme.color.black,
  fontSize: "1rem",
  lineHeight: "1.6rem",
  fontWeight: theme.fontWeight.medium,
}));

const ColorCardSummary = styled.div(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  flexWrap: "wrap",
  gap: "0.4rem",
  fontSize: "1.2rem",
  lineHeight: "1.8rem",
  color: theme.color.light_gray,
}));

const SummaryDot = styled.span(({ theme }) => ({
  width: "0.3rem",
  height: "0.3rem",
  borderRadius: "50%",
  backgroundColor: theme.color.light_gray,
  display: "inline-block",
}));

const DeleteButton = styled.button(({ theme }) => ({
  width: "2.8rem",
  height: "2.8rem",
  borderRadius: "999px",
  border: `1px solid ${theme.color.border_gray}`,
  backgroundColor: theme.color.white,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  cursor: "pointer",
  ":hover": {
    borderColor: theme.color.mint,
  },
}));

const ColorCardFoot = styled.div({
  display: "flex",
  justifyContent: "flex-start",
});

const SizePills = styled.div({
  display: "flex",
  flexWrap: "wrap",
  gap: "0.5rem",
});

const SizePill = styled.span(({ theme }) => ({
  padding: "0.4rem 0.8rem",
  borderRadius: "999px",
  border: `1px solid ${theme.color.border_gray}`,
  backgroundColor: theme.color.white,
  fontSize: "1.1rem",
  lineHeight: "1.6rem",
  color: theme.color.black,
}));

const SizeTypeField = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: "0.6rem",
});

const SizeTypeLabel = styled.div(({ theme }) => ({
  fontSize: "1.4rem",
  fontWeight: theme.fontWeight.medium,
  lineHeight: "2rem",
  color: theme.color.black,
}));

const SizeTypeHelp = styled.div(({ theme }) => ({
  fontSize: "1.2rem",
  lineHeight: "1.8rem",
  color: theme.color.light_gray,
}));

const ChipGroup = styled.div({
  display: "flex",
  flexWrap: "wrap",
  gap: "0.8rem",
});

const SizeTypeChip = styled.button<{ active: boolean }>(({ theme, active }) => ({
  padding: "0.8rem 1.2rem",
  borderRadius: "2rem",
  fontSize: "1.3rem",
  lineHeight: "1.8rem",
  cursor: "pointer",
  border: `1px solid ${active ? theme.color.green : theme.color.border_gray}`,
  backgroundColor: active ? theme.color.green : theme.color.white,
  color: active ? theme.color.white : theme.color.black,
}));

const EditorShell = styled.div(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "1.2rem",
  padding: "1.6rem",
  borderRadius: "1.4rem",
  border: `1px solid ${theme.color.border_gray}`,
  backgroundColor: theme.color.white,
}));

const EditorHeader = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: "0.4rem",
});

const EditorTitle = styled.div(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "0.8rem",
  fontSize: "1.4rem",
  fontWeight: theme.fontWeight.medium,
  color: theme.color.black,
}));

const EditorSubTitle = styled.div(({ theme }) => ({
  fontSize: "1.2rem",
  lineHeight: "1.8rem",
  color: theme.color.light_gray,
}));

const HelperMessage = styled.div(({ theme }) => ({
  fontSize: "1.2rem",
  lineHeight: "1.8rem",
  color: theme.color.light_gray,
}));

const ErrorMessage = styled.div(({ theme }) => ({
  color: theme.color.orange,
  fontSize: "1.2rem",
  lineHeight: "1.8rem",
}));

const ErrorStack = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: "0.2rem",
});

const EditorEmptyState = styled.div(({ theme }) => ({
  padding: "1.4rem",
  borderRadius: "1.4rem",
  border: `1px dashed ${theme.color.border_gray}`,
  color: theme.color.light_gray,
  fontSize: "1.2rem",
  lineHeight: "1.8rem",
}));
